import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, Heart, LogOut, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";

export const Logo = ({ size = "text-2xl" }) => (
  <Link to="/" data-testid="logo-link" className={`font-display font-bold ${size} tracking-tight text-white`}>
    PLAY<span className="text-[#D4AF37]">A</span>
  </Link>
);

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useCart();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header data-testid="site-header" className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "glass" : "bg-transparent"}`}>
      <div className="container-luxe flex items-center justify-between h-20">
        <Logo />
        <nav className="hidden lg:flex items-center gap-10">
          <Link data-testid="nav-shop" to="/shop" className="nav-link">Shop</Link>
          <Link data-testid="nav-categories" to="/categories" className="nav-link">Categories</Link>
          <Link data-testid="nav-about" to="/about" className="nav-link">About</Link>
          <Link data-testid="nav-faq" to="/faq" className="nav-link">FAQ</Link>
          <Link data-testid="nav-contact" to="/contact" className="nav-link">Contact</Link>
        </nav>
        <div className="flex items-center gap-5">
          <Link data-testid="nav-search" to="/shop" className="text-gray-300 hover:text-[#D4AF37] transition"><Search size={18} strokeWidth={1.5} /></Link>
          {user && <Link data-testid="nav-wishlist" to="/dashboard?tab=wishlist" className="text-gray-300 hover:text-[#D4AF37] transition"><Heart size={18} strokeWidth={1.5} /></Link>}
          <Link data-testid="nav-cart" to="/cart" className="relative text-gray-300 hover:text-[#D4AF37] transition">
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
          </Link>
          {user ? (
            <div className="hidden lg:flex items-center gap-3">
              {user.role === "admin" && <Link data-testid="nav-admin" to="/admin" className="nav-link flex items-center gap-1"><Shield size={14} /> Admin</Link>}
              <Link data-testid="nav-dashboard" to="/dashboard" className="nav-link flex items-center gap-1"><User size={14} /> {user.name?.split(" ")[0] || "Account"}</Link>
              <button data-testid="btn-logout" onClick={logout} className="text-gray-300 hover:text-[#D4AF37]"><LogOut size={16} /></button>
            </div>
          ) : (
            <Link data-testid="nav-login" to="/login" className="hidden lg:inline nav-link">Login</Link>
          )}
          <button data-testid="btn-menu" onClick={() => setOpen(!open)} className="lg:hidden text-white">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-white/5">
          <div className="container-luxe py-6 flex flex-col gap-4">
            <Link to="/shop" className="nav-link">Shop</Link>
            <Link to="/categories" className="nav-link">Categories</Link>
            <Link to="/about" className="nav-link">About</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
            <Link to="/contact" className="nav-link">Contact</Link>
            {user ? (
              <>
                <Link to="/dashboard" className="nav-link">Dashboard</Link>
                {user.role === "admin" && <Link to="/admin" className="nav-link">Admin</Link>}
                <button onClick={logout} className="nav-link text-left">Logout</button>
              </>
            ) : (
              <Link to="/login" className="nav-link">Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/5 mt-32 pt-20 pb-10">
      <div className="container-luxe grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <Logo />
          <p className="text-gray-400 text-sm mt-4 leading-relaxed">
            Authorized reseller of premium digital products. Instant delivery. Impeccable curation.
          </p>
        </div>
        <div>
          <h4 className="text-white text-sm tracking-widest uppercase mb-4">Shop</h4>
          <ul className="space-y-3 text-sm text-gray-400">
            <li><Link className="hover:text-[#D4AF37]" to="/shop">All Products</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/categories">Categories</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/shop?filter=new">New Arrivals</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/shop?filter=best">Best Sellers</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white text-sm tracking-widest uppercase mb-4">Company</h4>
          <ul className="space-y-3 text-sm text-gray-400">
            <li><Link className="hover:text-[#D4AF37]" to="/about">About</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/contact">Contact</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/faq">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white text-sm tracking-widest uppercase mb-4">Legal</h4>
          <ul className="space-y-3 text-sm text-gray-400">
            <li><Link className="hover:text-[#D4AF37]" to="/refund">Refund Policy</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/privacy">Privacy Policy</Link></li>
            <li><Link className="hover:text-[#D4AF37]" to="/terms">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>
      <div className="container-luxe mt-16 pt-8 border-t border-white/5 text-center text-xs text-gray-500 tracking-widest uppercase">
        © {new Date().getFullYear()} PLAYA. Licensed Digital Reseller. All rights reserved.
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      <Header />
      <main className="pt-20">{children}</main>
      <Footer />
    </div>
  );
}
